import { authenticatedFetch } from "./apiClient";
import { rateLimitToastMessage, parseRetryAfterSeconds } from "../utils/httpRateLimit";
import {
  isSimulateInflightErrorSlug,
  simulateInflightToastCopy,
} from "../utils/simulateQuotaToasts";
import { parseSimulateBacktestMeta } from "./backtestsApi";
import type { BacktestSummary } from "../types/backtests";
import type {
  PolicyTreeV2,
  SimulateCandleBudgetPreviewQuery,
  SimulateCandleBudgetPreviewResponse,
  TradePolicyFilterCatalogResponse,
  TradePolicySimConfigRecord,
  TradePolicySimConfigUpsertRequest,
  TradePolicySimulateRequest,
  TradePolicySimulateResponse,
  TradePolicySimulateJobEnvelope,
  TradePolicySimulateJobOutcome,
  TradePolicySimulateJobStartResponse,
} from "../types/tradePolicy";
import { extractSimulatePolicyTreeV2, simulatePolicySchemaVersion } from "../types/tradePolicy";
import type { SimulateLegs } from "../types/managementExplorer";
import type { WorkspaceNodeMeta } from "../types/workspace";
import {
  sanitizeSimulateReportId,
  sanitizeWorkspaceSimulateAttachFields,
} from "../utils/workspacePersistIds";
import { PaywallRequiredError } from "../utils/paywallErrors";
import {
  MarketDataUnresolvedError,
  parseMarketDataUnresolvedDetail,
} from "../utils/marketDataResolution";
import {
  MarketDataUnavailableError,
  throwIfMarketDataUnavailable,
} from "../utils/marketDataUnavailable";
import { parseApiErrorBody } from "../utils/parseApiError";
import { ApiRequestError, throwFromErrorBody } from "../utils/apiRequestError";

export class SimulateInflightLimitError extends Error {
  readonly forReport: boolean;

  constructor(message: string, options?: { forReport?: boolean }) {
    super(message);
    this.name = "SimulateInflightLimitError";
    this.forReport = Boolean(options?.forReport);
  }
}

function throwIfSimulateInflight(
  response: Response,
  slug: string | undefined,
  detail: string | undefined
): void {
  if (!isSimulateInflightErrorSlug(slug)) return;
  if (response.status !== 409 && response.status !== 429) return;
  const forReport = false;
  const copy = simulateInflightToastCopy({ forReport, backendMessage: detail });
  throw new SimulateInflightLimitError(copy.description, { forReport });
}

export type SaveSimulateJobResult =
  | { ok: true; backtestId?: string; backtestSummary?: BacktestSummary }
  | { ok: false; detail: string; slug?: string };

export class SimulateTradePolicyJobStillRunningError extends Error {
  jobId: string;
  constructor(jobId: string, message?: string) {
    super(message || "Simulation is still running. Please retry in a moment.");
    this.name = "SimulateTradePolicyJobStillRunningError";
    this.jobId = jobId;
  }
}

/**
 * How long FE will poll a simulate job before surfacing StillRunning.
 * Keep under Redis result TTL (~1h) so we can still save when a long hybrid finishes.
 */
export const SIMULATE_JOB_POLL_TIMEOUT_MS = 55 * 60_000;

export class ReportPolicySimConfigConflictError extends Error {
  current: TradePolicySimConfigRecord | null;
  constructor(current: TradePolicySimConfigRecord | null, message?: string) {
    super(message || "Policy simulator config is stale. Please reload.");
    this.name = "ReportPolicySimConfigConflictError";
    this.current = current;
  }
}

async function parseErrorBody(
  response: Response
): Promise<{ detail?: string; slug?: string; raw?: unknown }> {
  const error = await response.json().catch(() => ({}));
  const o = error && typeof error === "object" ? (error as Record<string, unknown>) : {};
  const parsed = parseApiErrorBody(o, response.statusText || "Request failed.");
  return { detail: parsed.message, slug: parsed.slug, raw: error };
}

async function throwIfPolicyResponseNotOk(response: Response, fallbackMessage: string): Promise<void> {
  if (response.ok) return;
  if (response.status === 401) {
    throw new ApiRequestError("Session expired. Please log in again.", 401, {
      slug: "auth.token_expired",
      message: "Session expired. Please log in again.",
    });
  }
  const parsed = await parseErrorBody(response);
  throwPolicyApiError(response, parsed, fallbackMessage);
}

function throwIfMarketDataUnresolved(response: Response, raw: unknown): void {
  if (response.status !== 422) return;
  throwIfMarketDataUnavailable(response, raw);
  const unresolved = parseMarketDataUnresolvedDetail(raw);
  if (unresolved) {
    throw new MarketDataUnresolvedError(unresolved, 422);
  }
}

function throwPolicyApiError(
  response: Response,
  parsed: { detail?: string; slug?: string; raw?: unknown },
  fallbackMessage: string
): never {
  throwIfMarketDataUnresolved(response, parsed.raw);
  throwIfSimulateInflight(response, parsed.slug, parsed.detail);
  if (response.status === 402) {
    throw new PaywallRequiredError();
  }
  if (response.status === 429 && !isSimulateInflightErrorSlug(parsed.slug)) {
    throw new ApiRequestError(rateLimitToastMessage(parseRetryAfterSeconds(response)), response.status, {
      slug: parsed.slug ?? "rate_limit.trade_policy_simulate",
      message: rateLimitToastMessage(parseRetryAfterSeconds(response)),
    });
  }
  throwFromErrorBody(
    response.status,
    parsed.raw && typeof parsed.raw === "object" ? parsed.raw : {},
    parsed.detail || fallbackMessage
  );
}

/** Shared in-flight + session cache so parallel mounts (drawers, simulator) share one GET. */
let filterCatalogInFlight: Promise<TradePolicyFilterCatalogResponse> | null = null;
let filterCatalogCached: TradePolicyFilterCatalogResponse | null = null;

/** No long-lived cache across version bumps; callers can clear via `clearTradePolicyFilterCatalogCache`. */
export function clearTradePolicyFilterCatalogCache(): void {
  filterCatalogCached = null;
  filterCatalogInFlight = null;
}

/**
 * GET /v1/trade-policy/filter-catalog.
 * Dedupes concurrent callers; caches the last successful response for the session.
 * Invalidate with `clearTradePolicyFilterCatalogCache` when catalog `version` changes.
 */
export async function fetchTradePolicyFilterCatalog(): Promise<TradePolicyFilterCatalogResponse> {
  if (filterCatalogCached) return filterCatalogCached;
  if (filterCatalogInFlight) return filterCatalogInFlight;

  filterCatalogInFlight = (async () => {
    const response = await authenticatedFetch("/v1/trade-policy/filter-catalog");

    if (!response.ok) {
      await throwIfPolicyResponseNotOk(response, `Failed to load filter catalog: ${response.status}`);
    }

    const data = (await response.json()) as TradePolicyFilterCatalogResponse;
    filterCatalogCached = data;
    return data;
  })();

  try {
    return await filterCatalogInFlight;
  } finally {
    filterCatalogInFlight = null;
  }
}

export async function fetchReportPolicySimConfig(reportId: string): Promise<TradePolicySimConfigRecord | null> {
  const encoded = encodeURIComponent(reportId);
  // BACKEND_API_URL is `/api`, so these resolve to `/api/reports/...` and `/api/v1/reports/...`.
  const candidates = [
    `/reports/${encoded}/policy-sim-config`,
    `/v1/reports/${encoded}/policy-sim-config`,
  ];
  let response: Response | null = null;
  let lastNetworkError: unknown = null;
  for (const endpoint of candidates) {
    try {
      const res = await authenticatedFetch(endpoint);
      // 404 can mean either "no saved config" or "route not found".
      // Keep trying fallbacks until we get a non-404 or exhaust all candidates.
      if (res.status === 404) {
        response = res;
        continue;
      }
      response = res;
      break;
    } catch (e) {
      lastNetworkError = e;
      continue;
    }
  }
  if (!response) {
    if (lastNetworkError instanceof Error) throw lastNetworkError;
    throw new Error("Failed to reach policy simulator config endpoint.");
  }
  if (response.status === 404) return null;
  if (!response.ok) {
    await throwIfPolicyResponseNotOk(response, `Failed to load policy simulator config: ${response.status}`);
  }
  const json = (await response.json()) as TradePolicySimConfigRecord;
  // Empty GET shape may omit revision — treat as 0 (create). Existing rows are usually ≥1.
  if (typeof json.revision !== "number" || !Number.isFinite(json.revision)) {
    return { ...json, revision: 0 };
  }
  return json;
}

export async function upsertReportPolicySimConfig(
  reportId: string,
  body: TradePolicySimConfigUpsertRequest
): Promise<TradePolicySimConfigRecord> {
  const encoded = encodeURIComponent(reportId);
  const candidates = [
    `/reports/${encoded}/policy-sim-config`,
    `/v1/reports/${encoded}/policy-sim-config`,
  ];
  let response: Response | null = null;
  let lastNetworkError: unknown = null;
  const revision =
    typeof body.revision === "number" && Number.isFinite(body.revision) ? body.revision : 0;
  const payload: TradePolicySimConfigUpsertRequest = { ...body, revision };
  for (const endpoint of candidates) {
    try {
      const res = await authenticatedFetch(endpoint, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      // 404 here likely indicates route mismatch; try next candidate.
      if (res.status === 404) {
        response = res;
        continue;
      }
      response = res;
      break;
    } catch (e) {
      lastNetworkError = e;
      continue;
    }
  }
  if (!response) {
    if (lastNetworkError instanceof Error) throw lastNetworkError;
    throw new Error("Failed to reach policy simulator config endpoint.");
  }
  if (!response.ok) {
    if (response.status === 401) {
      throw new ApiRequestError("Session expired. Please log in again.", 401, {
        slug: "auth.token_expired",
        message: "Session expired. Please log in again.",
      });
    }
    if (response.status === 402) throw new PaywallRequiredError();
    if (response.status === 409) {
      const raw = (await response.json().catch(() => null)) as any;
      const current = (raw?.detail?.current ?? null) as TradePolicySimConfigRecord | null;
      throw new ReportPolicySimConfigConflictError(current, "Policy simulator config has a newer revision.");
    }
    const parsed = await parseErrorBody(response);
    throwPolicyApiError(response, parsed, `Failed to save policy simulator config: ${response.status}`);
  }
  return (await response.json()) as TradePolicySimConfigRecord;
}

export async function simulateTradePolicy(
  body: TradePolicySimulateRequest
): Promise<TradePolicySimulateResponse> {
  const response = await authenticatedFetch("/v1/trade-policy/simulate", {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new ApiRequestError("Session expired. Please log in again.", 401, {
        slug: "auth.token_expired",
        message: "Session expired. Please log in again.",
      });
    }
    const parsed = await parseErrorBody(response);
    throwPolicyApiError(response, parsed, `Simulation failed: ${response.status}`);
  }

  const json = (await response.json()) as unknown;
  // If the server returns a job envelope, callers must poll.
  if (
    json &&
    typeof json === "object" &&
    !Array.isArray(json) &&
    ("job_id" in (json as any) || "status" in (json as any)) &&
    !("baseline" in (json as any) || "coverage" in (json as any))
  ) {
    // Keep backwards compat by throwing a clear error if a caller hasn't been migrated.
    throw new Error("Simulation started as an async job. Call simulateTradePolicyStart() instead.");
  }
  return json as TradePolicySimulateResponse;
}

export async function simulateTradePolicyStart(
  body: TradePolicySimulateRequest
): Promise<TradePolicySimulateResponse | TradePolicySimulateJobStartResponse> {
  const reportId = sanitizeSimulateReportId(body.report_id);
  const sanitizedBody: TradePolicySimulateRequest = {
    ...body,
    ...(reportId ? { report_id: reportId } : {}),
  };
  if (!reportId && "report_id" in sanitizedBody) {
    delete sanitizedBody.report_id;
  }

  const postOnce = async () => {
    const response = await authenticatedFetch("/v1/trade-policy/simulate", {
      method: "POST",
      body: JSON.stringify(sanitizedBody),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new ApiRequestError("Session expired. Please log in again.", 401, {
          slug: "auth.token_expired",
          message: "Session expired. Please log in again.",
        });
      }
      const parsed = await parseErrorBody(response);
      throwPolicyApiError(response, parsed, `Simulation failed: ${response.status}`);
    }

    return (await response.json()) as TradePolicySimulateResponse | TradePolicySimulateJobStartResponse;
  };

  try {
    return await postOnce();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // Docker Desktop / Redis blips often surface as a bare network drop on the first POST.
    if (/could not reach the api|failed to fetch|networkerror|load failed/i.test(message)) {
      await sleep(1200);
      return await postOnce();
    }
    throw err;
  }
}

export type SaveSimulateJobOverrides = {
  policy_tree?: PolicyTreeV2;
  simulate_legs?: SimulateLegs;
  workspaceId?: string;
  parentNodeId?: string;
  position?: { x: number; y: number };
  meta?: WorkspaceNodeMeta;
};

export async function saveSimulateTradePolicyJob(
  jobId: string,
  overrides?: SaveSimulateJobOverrides
): Promise<SaveSimulateJobResult> {
  const attach = sanitizeWorkspaceSimulateAttachFields({
    workspaceId: overrides?.workspaceId,
    parentNodeId: overrides?.parentNodeId,
    position: overrides?.position,
    meta: overrides?.meta,
  });
  const body =
    overrides &&
    (overrides.policy_tree != null ||
      overrides.simulate_legs != null ||
      attach.workspaceId != null ||
      attach.parentNodeId != null ||
      attach.position != null ||
      attach.meta != null)
      ? {
          ...(overrides.policy_tree != null ? { policy_tree: overrides.policy_tree } : {}),
          ...(overrides.simulate_legs != null ? { simulate_legs: overrides.simulate_legs } : {}),
          ...(attach.workspaceId != null ? { workspaceId: attach.workspaceId } : {}),
          ...(attach.parentNodeId != null ? { parentNodeId: attach.parentNodeId } : {}),
          ...(attach.position != null ? { position: attach.position } : {}),
          ...(attach.meta != null ? { meta: attach.meta } : {}),
        }
      : undefined;
  const response = await authenticatedFetch(`/v1/trade-policy/simulate/jobs/${encodeURIComponent(jobId)}/save`, {
    method: "POST",
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (response.ok) {
    const raw = await response.json().catch(() => null);
    const { backtestId, backtestSummary } = parseSimulateBacktestMeta(raw);
    return {
      ok: true,
      ...(backtestId ? { backtestId } : {}),
      ...(backtestSummary ? { backtestSummary } : {}),
    };
  }
  const { detail, slug } = await parseErrorBody(response);
  return {
    ok: false,
    detail: detail || `Failed to save simulation: ${response.status}`,
    ...(slug ? { slug } : {}),
  };
}

export async function fetchSimulateTradePolicyJob(jobId: string): Promise<TradePolicySimulateJobEnvelope> {
  const loadOnce = async () => {
    const response = await authenticatedFetch(`/v1/trade-policy/simulate/jobs/${encodeURIComponent(jobId)}`);
    if (!response.ok) {
      if (response.status === 404) {
        const parsed = await parseErrorBody(response);
        throwPolicyApiError(response, parsed, "Simulation job expired. Please rerun.");
      }
      await throwIfPolicyResponseNotOk(response, `Failed to load simulation job: ${response.status}`);
    }
    return (await response.json()) as TradePolicySimulateJobEnvelope;
  };

  try {
    return await loadOnce();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (/could not reach the api|failed to fetch|networkerror|load failed/i.test(message)) {
      await sleep(800);
      return await loadOnce();
    }
    throw err;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export async function waitForSimulateTradePolicyJob(
  jobId: string,
  opts?: { timeoutMs?: number }
): Promise<TradePolicySimulateJobOutcome> {
  const timeoutMs = opts?.timeoutMs ?? SIMULATE_JOB_POLL_TIMEOUT_MS;
  const started = Date.now();
  let attempt = 0;
  while (true) {
    attempt += 1;
    const job = await fetchSimulateTradePolicyJob(jobId);
    if (job.status === "succeeded") {
      if (!job.result) throw new Error("Job succeeded but missing result.");
      const meta = parseSimulateBacktestMeta(job);
      const policyTree =
        extractSimulatePolicyTreeV2(job) ?? extractSimulatePolicyTreeV2(job.request);
      const policySchemaVersion = simulatePolicySchemaVersion(job);
      return {
        result: job.result,
        ...(meta.backtestId ? { backtestId: meta.backtestId } : {}),
        ...(meta.backtestSummary ? { backtestSummary: meta.backtestSummary } : {}),
        ...(policyTree ? { policyTree } : {}),
        ...(policySchemaVersion ? { policySchemaVersion } : {}),
      };
    }
    if (job.status === "failed") {
      throw new Error(job.error || "Simulation job failed.");
    }
    if (Date.now() - started > timeoutMs) {
      throw new SimulateTradePolicyJobStillRunningError(
        jobId,
        "Simulation is still running on the server (queued/running). You can retry polling to fetch the result."
      );
    }
    const elapsed = Date.now() - started;
    const delay = elapsed < 5_000 ? 500 : elapsed < 30_000 ? 1_000 : 2_000;
    await sleep(delay);
    // minor jitter to avoid thundering herd
    if (attempt % 10 === 0) await sleep(50);
  }
}

function buildSimulateCandleBudgetPreviewParams(
  query: SimulateCandleBudgetPreviewQuery
): URLSearchParams {
  const params = new URLSearchParams();
  params.append("symbol", query.symbol);
  params.append("candle_timeframe", query.candle_timeframe);
  if (query.broker_account_code) params.append("broker_account_code", query.broker_account_code);
  if (query.start_date) params.append("start_date", query.start_date);
  if (query.end_date) params.append("end_date", query.end_date);
  if (query.timeframe) params.append("timeframe", query.timeframe);
  if (query.local_start_date) params.append("local_start_date", query.local_start_date);
  if (query.local_end_date) params.append("local_end_date", query.local_end_date);
  if (query.timezone) params.append("timezone", query.timezone);
  if (query.report_id) params.append("report_id", query.report_id);
  if (query.entry_candle_timeframe) {
    params.append("entry_candle_timeframe", query.entry_candle_timeframe);
  }
  if (query.management_candle_timeframe) {
    params.append("management_candle_timeframe", query.management_candle_timeframe);
  }
  return params;
}

/**
 * GET /v1/trade-policy/simulate/candle-budget-preview
 * No coarsen — returns whether the window fits at the requested TF and suggested periods.
 */
export async function fetchSimulateCandleBudgetPreview(
  query: SimulateCandleBudgetPreviewQuery
): Promise<SimulateCandleBudgetPreviewResponse> {
  const params = buildSimulateCandleBudgetPreviewParams(query);
  const response = await authenticatedFetch(
    `/v1/trade-policy/simulate/candle-budget-preview?${params.toString()}`
  );
  if (!response.ok) {
    if (response.status === 401) {
      throw new ApiRequestError("Session expired. Please log in again.", 401, {
        slug: "auth.token_expired",
        message: "Session expired. Please log in again.",
      });
    }
    const parsed = await parseErrorBody(response);
    throwPolicyApiError(
      response,
      parsed,
      `Failed to fetch candle budget preview: ${response.status}`
    );
  }
  return (await response.json()) as SimulateCandleBudgetPreviewResponse;
}
